import { useMemo, useState } from 'react';

import { Box, Checkbox, Divider, Stack, Typography } from '@mui/material';

import { StlButton } from 'features/common/StlButton/StlButton';
import { light_gray_text } from 'theme/cemTheme';

import { filterWords } from '../alertHelpers';
import { FilterSearchBar } from '../components/FilterSearchBar';

import styles from './SelectableList.module.css';

interface SelectableListProps {
  mode: 'view' | 'edit';
  title: string;
  selectedItems: string[];
  availableItems?: string[];
  onSelectionChange?: (items: string[]) => void;
  caption: string | ((selectedCount: number) => string);
  emptyStateMessage?: string;
  searchPlaceholder?: string;
  width?: string;
}

export function SelectableList({
  mode,
  title,
  selectedItems,
  availableItems = [],
  onSelectionChange,
  caption,
  emptyStateMessage = 'No items found',
  searchPlaceholder = 'Search',
  width = '100%',
}: SelectableListProps) {
  const isEdit = mode === 'edit';
  const [searchTerm, setSearchTerm] = useState('');
  const [showSelected, setShowSelected] = useState(false);

  const sortedAvailableItems = useMemo(
    () =>
      Array.from(new Set(availableItems)).sort((a, b) => a.localeCompare(b)),
    [availableItems],
  );

  const selectedSet = useMemo(
    () => new Set(selectedItems.map((item) => item.toLowerCase())),
    [selectedItems],
  );
  const selectedCount = selectedItems.length;

  // In view mode, just show selected items. In edit mode, show available items or selected items
  const baseList = useMemo(() => {
    if (!isEdit) {
      return selectedItems;
    }
    return showSelected ? selectedItems : sortedAvailableItems;
  }, [isEdit, showSelected, selectedItems, sortedAvailableItems]);

  // apply filter on the base list
  const visibleItems = useMemo(
    () => (searchTerm.trim() ? filterWords(baseList, searchTerm) : baseList),
    [baseList, searchTerm],
  );

  const handleToggle = (item: string) => {
    const itemLower = item.toLowerCase();
    // find if this item is already in selectedItems
    const existingItem = selectedItems.find(
      (selected) => selected.toLowerCase() === itemLower,
    );

    if (existingItem) {
      // Remove the existing item
      const next = selectedItems.filter(
        (selected) => selected.toLowerCase() !== itemLower,
      );
      onSelectionChange?.(next.sort((a, b) => a.localeCompare(b)));
    } else {
      // add the new item
      const next = [...selectedItems, item];
      onSelectionChange?.(next.sort((a, b) => a.localeCompare(b)));
    }
  };

  const handleShowSelected = () => setShowSelected((s) => !s);

  const captionText =
    typeof caption === 'function' ? caption(selectedCount) : caption;

  return (
    <Stack gap={1} className={styles.tabPanels} width={width}>
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4">{title}</Typography>
          {isEdit && (
            <StlButton
              width="8rem"
              variant="text"
              value={
                showSelected ? 'Show All' : `Show Selected (${selectedCount})`
              }
              handleClick={handleShowSelected}
            />
          )}
        </Stack>
        <Typography variant="caption" color={light_gray_text}>
          {captionText}
        </Typography>
      </Box>
      {isEdit ? (
        <FilterSearchBar
          searchTerm={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={searchPlaceholder}
          hasFrame={false}
        />
      ) : (
        <Divider />
      )}
      <Stack className={styles.listContainer}>
        {visibleItems.length === 0 ? (
          <Typography variant="h4">{emptyStateMessage}</Typography>
        ) : (
          visibleItems.map((item) => {
            const isSelected = selectedSet.has(item.toLowerCase());
            const itemClasses = [
              styles.listItem,
              isEdit ? styles.listItemEdit : styles.listItemView,
              isEdit && isSelected
                ? styles.listItemSelected
                : styles.listItemUnselected,
            ].join(' ');
            return (
              <Stack
                direction="row"
                alignItems="center"
                key={item}
                className={itemClasses}
                onClick={isEdit ? () => handleToggle(item) : undefined}
              >
                {isEdit && (
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggle(item)}
                    size="small"
                  />
                )}
                <Typography variant="h4" className={styles.recipientItem}>
                  {item}
                </Typography>
              </Stack>
            );
          })
        )}
      </Stack>
    </Stack>
  );
}
